import React, { useMemo } from 'react';
import { Select, Spin, App } from 'antd';
import type { SelectProps } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { EpcOpportunitySelect } from '@/components/epc';
import { listContractReviews } from '@/services/contractReview.service';
import { listProductionRequests } from '@/services/productionRequest.service';
import salesOrderService from '@/services/salesOrder.service';
import { epcCommercialService } from '@/services/epcCommercial.service';
import cspmService from '@/services/cspm.service';
import type { CspmSourceType } from '@/constants/cspm';

export type CspmSourceValidated = {
  source_document_type: string;
  source_document_id: string;
  customer_id: string;
  label: string;
};

export type CspmSourceDocumentSelectProps = Omit<
  SelectProps<string>,
  'options' | 'loading' | 'showSearch' | 'optionFilterProp'
> & {
  sourceType?: CspmSourceType | string | null;
  customerId?: string | null;
  /** When set, validates via POST /cspm/sources/validate and returns customer + label. */
  onValidated?: (result: CspmSourceValidated | null) => void;
};

/**
 * Searchable source document picker for CSPM — avoids raw UUID entry when a list API exists.
 */
export const CspmSourceDocumentSelect: React.FC<CspmSourceDocumentSelectProps> = ({
  sourceType,
  customerId,
  allowClear = true,
  placeholder,
  disabled,
  onChange,
  onValidated,
  ...rest
}) => {
  const { message } = App.useApp();
  const type = String(sourceType || '');

  const { data: salesOrders = [], isLoading: loadingSo } = useQuery({
    queryKey: ['cspm', 'source-picker', 'sales_order', customerId],
    queryFn: () =>
      salesOrderService.getAll({
        customer_id: customerId || undefined,
      }),
    enabled: type === 'sales_order',
  });

  const { data: contractReviews = [], isLoading: loadingCr } = useQuery({
    queryKey: ['cspm', 'source-picker', 'contract_review', customerId],
    queryFn: async () => {
      const res = await listContractReviews({
        customer_id: customerId || undefined,
        take: '100',
      });
      return res.data;
    },
    enabled: type === 'contract_review',
  });

  const { data: productionRequests = [], isLoading: loadingPr } = useQuery({
    queryKey: ['cspm', 'source-picker', 'production_request'],
    queryFn: () => listProductionRequests(),
    enabled: type === 'production_request',
  });

  const { data: epcContracts = [], isLoading: loadingEpc } = useQuery({
    queryKey: ['cspm', 'source-picker', 'epc_contract'],
    queryFn: async () => {
      const rows = await epcCommercialService.listContracts();
      return Array.isArray(rows) ? rows : [];
    },
    enabled: type === 'epc_contract',
  });

  const options = useMemo(() => {
    if (type === 'sales_order') {
      const list = Array.isArray(salesOrders) ? salesOrders : [];
      const filtered = customerId
        ? list.filter((o: { customer_id?: string }) => o.customer_id === customerId)
        : list;
      return filtered.map(
        (o: { id: string; order_number?: string; sales_order_number?: string; status?: string }) => ({
          value: o.id,
          label: `${o.order_number || o.sales_order_number || o.id}${o.status ? ` (${o.status})` : ''}`,
        })
      );
    }
    if (type === 'contract_review') {
      const list = Array.isArray(contractReviews) ? contractReviews : [];
      const filtered = customerId
        ? list.filter((r) => !r.customer_id || r.customer_id === customerId)
        : list;
      return filtered.map((r) => ({
        value: r.id,
        label: `${r.review_number}${r.review_status ? ` (${r.review_status})` : ''}`,
      }));
    }
    if (type === 'production_request') {
      const list = Array.isArray(productionRequests) ? productionRequests : [];
      const filtered = customerId
        ? list.filter((r) => !r.customer_id || r.customer_id === customerId)
        : list;
      return filtered.map((r) => ({
        value: r.id,
        label: `${r.request_number}${r.status ? ` (${r.status})` : ''}`,
      }));
    }
    if (type === 'epc_contract') {
      return (epcContracts as Array<Record<string, unknown>>).map((c) => {
        const id = String(c.id || '');
        const num = String(c.contract_number || c.number || c.contract_name || c.name || id);
        const status = c.status ? ` (${String(c.status)})` : '';
        return { value: id, label: `${num}${status}` };
      });
    }
    return [];
  }, [type, salesOrders, contractReviews, productionRequests, epcContracts, customerId]);

  const runValidate = async (documentId: string | undefined) => {
    if (!onValidated) return;
    if (!documentId || !type) {
      onValidated(null);
      return;
    }
    try {
      const result = await cspmService.validateSource({
        source_document_type: type,
        source_document_id: documentId,
        customer_id: customerId || undefined,
      });
      if (result && typeof result === 'object' && (result as CspmSourceValidated).customer_id) {
        onValidated(result as CspmSourceValidated);
      }
    } catch (e: any) {
      message.error(e?.response?.data?.message || e?.message || 'Source validation failed');
      onValidated(null);
    }
  };

  const handleChange: SelectProps<string>['onChange'] = (value, option) => {
    onChange?.(value, option);
    void runValidate(value);
  };

  if (type === 'opportunity') {
    return (
      <EpcOpportunitySelect
        {...rest}
        customerId={customerId}
        allowClear={allowClear}
        disabled={disabled}
        placeholder={placeholder || 'Select opportunity'}
        onChange={(value, option) => {
          onChange?.(value, option);
          void runValidate(value);
        }}
      />
    );
  }

  if (!type) {
    return (
      <Select
        {...rest}
        allowClear={allowClear}
        disabled
        placeholder={placeholder || 'Select source type first'}
        options={[]}
      />
    );
  }

  const loading = loadingSo || loadingCr || loadingPr || loadingEpc;

  return (
    <Select
      {...rest}
      allowClear={allowClear}
      disabled={disabled}
      placeholder={placeholder || 'Select source document'}
      loading={loading}
      showSearch
      optionFilterProp="label"
      options={options}
      onChange={handleChange}
      notFoundContent={loading ? <Spin size="small" /> : undefined}
    />
  );
};

export default CspmSourceDocumentSelect;
